import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import SignUpModal from '../SignUpModal';
import { useStravaErrorLogging } from '@strava/container-context';
import { AuthenticationData } from '../../../authentication.types';
import { devLog } from '@strava/utils';

export type SignUpModalVariants =
  | 'generic'
  | 'generic_time_delayed'
  | 'follow_athlete'
  | 'join_challenge'
  | 'kudos_comment'
  | 'running_tools'
  | 'activity_details'
  | 'view_profile'
  | 'find_routes'
  | 'see_route_details';

export type CustomHeader = { entityImgSrc: string; entityName: string };

export type SignUpModalContextState = {
  // opens the modal with the given variant and optional redirect url
  openSignUpModal: ({
    variant,
    redirectUrl,
    customHeader
  }: {
    variant: SignUpModalVariants;
    redirectUrl?: string;
    customHeader?: CustomHeader;
  }) => void;

  // keeps track of whether the modal is open
  isSignUpModalOpen: boolean;

  // closes the modal
  dismissSignUpModal: () => void;

  // the variant to be used by the modal
  variant: SignUpModalVariants | null;

  // optional redirect url to be used after the authentication flow
  redirectUrl?: string;

  // optional custom header to be used by the modal
  customHeader?: CustomHeader;
};

const SignUpModalContext = createContext<SignUpModalContextState | undefined>(
  undefined
);
SignUpModalContext.displayName = 'SignUpModalContext';

export const useSignUpModalContext = () => {
  const context = useContext(SignUpModalContext);

  // Fail fast if hook is used outside the provider
  if (!context) {
    throw new Error(
      'useSignUpModalContext must be used within the SignUpModalContext provider'
    );
  }

  return context;
};

type SignUpModalContextProviderProps = {
  /**
   * whatever children the context provider is wrapping
   * (ideally the parent container for whatever CTAs trigger the modal)
   */
  children: ReactNode;
  authenticationData?: AuthenticationData;
  category: string;
};

export const SignUpModalContextProvider = ({
  children,
  authenticationData,
  category
}: SignUpModalContextProviderProps) => {
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const [variant, setVariant] = useState<SignUpModalVariants | null>(null);
  const [redirectUrl, setRedirectUrl] =
    useState<SignUpModalContextState['redirectUrl']>();
  const [customHeader, setCustomHeader] =
    useState<SignUpModalContextState['customHeader']>();

  const { logError } = useStravaErrorLogging();

  const openSignUpModal: SignUpModalContextState['openSignUpModal'] =
    useCallback(
      ({ variant, redirectUrl, customHeader }) => {
        devLog(
          `[SUM] ::: openSignUpModal ${JSON.stringify(
            { variant, redirectUrl, customHeader },
            null,
            2
          )}}`
        );

        // validate required fields
        if (!variant) {
          logError(new Error('[SUM] ::: variant is required'));
          return;
        }
        if (!authenticationData) {
          logError(new Error('[SUM] ::: authenticationData is required'));
          return;
        }
        if (
          ['follow_athlete', 'view_profile'].includes(variant) &&
          !customHeader?.entityName
        ) {
          logError(
            new Error(
              `[SUM] ::: variant - '${variant}' requires the name for the entity.`
            )
          );
          return;
        }
        if (variant === 'follow_athlete' && !customHeader?.entityImgSrc) {
          logError(
            new Error(
              `[SUM] ::: variant - '${variant}' requires the image src for the entity.`
            )
          );
          return;
        }

        // transmit the data to the modal
        setVariant(variant);
        setRedirectUrl(redirectUrl);
        setCustomHeader(customHeader);

        // open the modal
        setIsSignUpModalOpen(true);
      },
      [authenticationData, logError]
    );

  const dismissSignUpModal = useCallback(() => {
    setIsSignUpModalOpen(false);
  }, []);

  const providerState = useMemo<SignUpModalContextState>(
    () => ({
      openSignUpModal,
      dismissSignUpModal,
      isSignUpModalOpen,
      variant,
      redirectUrl,
      customHeader
    }),
    [
      dismissSignUpModal,
      isSignUpModalOpen,
      openSignUpModal,
      variant,
      redirectUrl,
      customHeader
    ]
  );

  return (
    <SignUpModalContext.Provider value={providerState}>
      {authenticationData?.authorizationUrls && (
        <SignUpModal {...authenticationData} category={category} />
      )}
      {children}
    </SignUpModalContext.Provider>
  );
};
